import React, { createContext, useCallback, useContext, useState } from 'react';
import { Button } from './Button';

type MessageBoxOptions = { title?: string; message: string; okText?: string; copyText?: string; copyTextLabel?: string };

type MessageBoxContextType = (options: MessageBoxOptions) => Promise<void>;

const MessageBoxContext = createContext<MessageBoxContextType | undefined>(undefined);

export const MessageBoxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [opts, setOpts] = useState<MessageBoxOptions | null>(null);
  const [resolver, setResolver] = useState<() => void>(() => () => {});

  const show = useCallback<MessageBoxContextType>((options) => {
    setOpts(options);
    setVisible(true);
    return new Promise<void>(resolve => {
      setResolver(() => resolve);
    });
  }, []);

  const onClose = () => {
    setVisible(false);
    resolver();
  };

  const onCopy = async () => {
    if (!opts?.copyText) return;
    try { await navigator.clipboard.writeText(opts.copyText); } catch {}
  };

  return (
    <MessageBoxContext.Provider value={show}>
      {children}
      {visible && opts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={onClose} />
          <div className="relative z-10 w-full max-w-md rounded-md border bg-card p-4 shadow-lg">
            <div className="text-lg font-semibold">{opts.title || 'Message'}</div>
            <div className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap break-all">{opts.message}</div>
            <div className="mt-4 flex justify-end gap-2">
              {opts.copyText && (
                <Button variant="outline" onClick={onCopy}>{opts.copyTextLabel || 'Copy'}</Button>
              )}
              <Button onClick={onClose}>{opts.okText || 'OK'}</Button>
            </div>
          </div>
        </div>
      )}
    </MessageBoxContext.Provider>
  );
};

export const useMessageBox = () => {
  const ctx = useContext(MessageBoxContext);
  if (!ctx) throw new Error('useMessageBox must be used within MessageBoxProvider');
  return ctx;
};
